// Calculs sur le portefeuille — logique pure, reprise de poc/src/modele.js.
//
// Règles héritées du POC (poc/docs/adr/0005) :
//   R2 — aucun effectif en dur : tout se compte sur la donnée chargée ;
//   R3 — les ordres sont calculés ;
//   R6 — les cas dégénérés sont traités : ensemble vide, date absente.

import type { DateIso, EtatTemporel, Levier, Projet } from "./types";

export const comparerFr = (a: string, b: string): number => a.localeCompare(b, "fr");

export interface CouvertureLevier extends Levier {
  projets: number;
  /** `null` sur un ensemble vide : une part n'a pas de sens sans dénominateur. */
  part: number | null;
}

/** Couverture de chaque levier, par effectif décroissant puis libellé. */
export function couvertureParLevier(projets: Projet[], leviers: Levier[]): CouvertureLevier[] {
  return leviers
    .map((levier) => {
      const n = projets.filter((p) => p.leviers.includes(levier.id)).length;
      return { ...levier, projets: n, part: projets.length ? n / projets.length : null };
    })
    .sort((a, b) => b.projets - a.projets || comparerFr(a.libelle, b.libelle));
}

/** Date du jour au format ISO, dans le fuseau du navigateur. */
export function aujourdhuiIso(maintenant: Date = new Date()): DateIso {
  const mois = String(maintenant.getMonth() + 1).padStart(2, "0");
  const jour = String(maintenant.getDate()).padStart(2, "0");
  return `${maintenant.getFullYear()}-${mois}-${jour}`;
}

/**
 * État d'un projet à une date donnée. Jamais de date inventée : sans début ni fin,
 * l'état est « inconnu ».
 */
export function etatTemporel(
  projet: Pick<Projet, "dateDebut" | "dateFin">,
  aujourdhui: DateIso,
): EtatTemporel {
  if (!projet.dateDebut && !projet.dateFin) return "inconnu";
  if (projet.dateFin && projet.dateFin < aujourdhui) return "termine";
  if (projet.dateDebut && projet.dateDebut > aujourdhui) return "avenir";
  return "encours";
}

export const LIBELLES_ETAT: Record<EtatTemporel, string> = {
  encours: "En cours",
  avenir: "À venir",
  termine: "Terminé",
  inconnu: "Dates non renseignées",
};

/** Effectif par état, « inconnu » toujours en dernier. */
export function repartitionTemporelle(
  projets: Projet[],
  aujourdhui: DateIso,
): { etat: EtatTemporel; projets: number }[] {
  const ordre: EtatTemporel[] = ["encours", "avenir", "termine", "inconnu"];
  return ordre
    .map((etat) => ({
      etat,
      projets: projets.filter((p) => etatTemporel(p, aujourdhui) === etat).length,
    }))
    .filter((e) => e.projets > 0);
}

/** Les vrais leviers, sans les indicateurs dérivés (« Approches multi-leviers »). */
export const leviersPropres = (leviers: Levier[]): Levier[] =>
  leviers.filter((l) => l.nature === "levier");

export interface GroupeBioagresseur {
  /** « Lépidoptères », « Champignons »… ou le nom seul quand il n'y a pas de détail. */
  nom: string;
  /** Les espèces entre parenthèses, une par entrée ; vide sans parenthèses. */
  details: string[];
}

/**
 * Lit les bioagresseurs saisis en texte libre : chaque élément est soit un nom
 * (« Thrips »), soit un groupe et ses espèces séparées par « | » entre parenthèses
 * (« Lépidoptères (Noctuelle de la tomate|Carpocapse des prunes) »).
 */
export function groupesBioagresseurs(elements: readonly string[]): GroupeBioagresseur[] {
  return elements.flatMap((brut) => {
    const texte = brut.trim();
    if (!texte) return [];
    const m = /^(.*?)\s*\((.*)\)\s*$/.exec(texte);
    if (!m) return [{ nom: texte, details: [] }];
    const details = (m[2] ?? "")
      .split("|")
      .map((d) => d.trim())
      .filter(Boolean);
    const nom = (m[1] ?? "").trim();
    return [{ nom: nom || details.join(", "), details: nom ? details : [] }];
  });
}
